import { getUrl } from '@/utils/getUrl';
import { http } from '@/utils/http';
import { getCookie } from 'cookies-next';
import { decodeJwt } from 'jose';
import { NextApiRequest } from 'next';
import { getOcelotConfig } from 'lib/configs/ocelot';

type Context = {
  cookies?: { [key: string]: string } | Partial<{ [key: string]: string }>;
  query?: { [key: string]: string | string[] | undefined };
};

export type AlltIdJwtToken = {
  data: {
    id: string;
    cc: string;
    open: boolean;
    user: {
      access: string | null;
      tags: string[];
    };
  };
};

export interface GetMyDataProps {
  articleId?: string;
  req?: NextApiRequest;
}

const myDataFactory = (endpoint: string) => (props?: GetMyDataProps) => {
  const token = getToken({ cookies: props?.req?.cookies });
  const userId = getUserId({ cookies: props?.req?.cookies });
  const articleId = props?.articleId;

  if (!userId || !endpoint) {
    return;
  }

  const path = [userId, endpoint, articleId].filter(Boolean).join('/');
  const url = getUrl(`/mydata/${path}`)?.href;

  if (!url || !token) {
    return;
  }

  return http.get(url, {
    headers: {
      'X-API-Token': token,
    },
  });
};

const getSavedArticlesV2 = () => http.get('/api/user-profile/get-saved-articles');

export const getSavedArticlesIds = () => {
  const loginV2Enabled = getOcelotConfig('features.login.v2.enabled');
  return loginV2Enabled ? getSavedArticlesV2() : myDataFactory('getSavedArticleIds')();
};

const toggleArticleV2 = (action: 'save' | 'remove', props?: GetMyDataProps) => {
  const articleId = props?.articleId;

  if (!articleId) {
    return;
  }

  const url = getUrl('/api/user-profile/toggle-article')?.href;

  if (!url) {
    return;
  }

  return http.post(url, { articleId, action });
};

export const saveArticle = (props?: GetMyDataProps) => {
  const loginV2Enabled = getOcelotConfig('features.login.v2.enabled');

  return loginV2Enabled ? toggleArticleV2('save', props) : myDataFactory('saveArticle')(props);
};

export const removeSavedArticle = (props?: GetMyDataProps) => {
  const loginV2Enabled = getOcelotConfig('features.login.v2.enabled');

  return loginV2Enabled ? toggleArticleV2('remove', props) : myDataFactory('removeSavedArticle')(props);
};

export const setFirstLoginTime = myDataFactory('setFirstLoginTime');

const getToken = (context?: Context) =>
  (context?.cookies?.login_token as string) || (context?.query?.code as string) || getCookie('login_token')?.toString();

const getUserId = (context?: Context) => {
  const token = getToken(context);

  const payload = (token ? decodeJwt(token) : {}) as AlltIdJwtToken;

  return payload?.data?.cc as string | undefined;
};

export const getUserTags = (context?: Context): string[] => {
  const token = getToken(context);
  const payload = (token ? decodeJwt(token) : {}) as AlltIdJwtToken;

  return payload?.data?.user?.tags || [];
};
